import React, { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { PropTypes } from 'prop-types';
import { Icon } from '@unite-us/ui';
import { get, startCase, isEmpty } from 'lodash';
import moment from 'moment';
import { Card, CardDetail, CardHeader } from 'common/Card';
import { Spinner } from 'common/spinners';
import formatDate from 'common/utils/Dates/formatDate';
import getTimeDifference from 'common/utils/Dates/getTimeDifference';
import cx from 'classnames';
import { epochToDate } from 'common/utils/utils';
import { usePopulate, useFind } from 'src/api/APIHooks';
import useClientNameRenderer from 'src/common/hooks/useClientNameRenderer';
import { useFeatureFlag } from 'common/hooks';
import { ReviewReasonDetailsCard } from 'common/Workqueue/ReviewReasonDetailsCard';
import { useAssigneeOptions, REQUEST_UNASSIGNED } from 'common/Workqueue/hooks/useAssigneeOptions';
import AssignUserToEnrollment from './AssignUserToEnrollment';
import { ENROLLMENT_STATUSES, REQUEST_TYPES } from '../constants';
import getClientStatusText from '../utils/getClientStatusText';
import { useSccRequiredInsurancePlan } from '../hooks';
import AcceptEnrollmentRequestButton from './AcceptEnrollmentRequestButton';
import EnrollmentStatusIndicator from './EnrollmentStatusIndicator';
import RejectEnrollmentRequestButton from './RejectEnrollmentRequestButton';
import MarkEnrollmentAsUrgentCheckbox from './MarkEnrollmentAsUrgentCheckbox';
import { NcTracksVerification } from './NcTracks/NcTracksVerification';
import { useFindEligibilityForInsurance } from '../hooks/useFindEligibilityForInsurance';
import { useCurrentProviderId } from '../../../common/contexts/CurrentProviderContext';
import { useEnrollmentReviewReasonOptions } from '../hooks/useEnrollmentReviewReasonOptions';
import { useReviewEnrollmentRequest } from '../hooks/useUpdateEnrollmentRequest';
import ReviewEnrollmentRequest from './ReviewEnrollmentRequest';

export const EnrollmentDrawerDetails = ({
  deselectEnrollment,
  enrollmentRequest,
  getNextRequest,
  getPrevRequest,
  setDrawerOpen,
  selectedEnrollments,
  onClientNameClick,
}) => {
  const iconClass = 'fill-current text-grey h-18';
  const [isLoading, setLoading] = useState(true);

  const isDisenrollType = enrollmentRequest.request_type === REQUEST_TYPES.disenroll;
  const isOpenStatus = enrollmentRequest.enrollment_status === ENROLLMENT_STATUSES.open;
  const pays6096UpdateEWQDesign = useFeatureFlag('pays-6096-update-ewq-design');
  const allowUserToMarkEnrollmentRequestInReview = useFeatureFlag('allow-user-to-mark-enrollment-request-in-review');
  const showReviewEnrollmentRequest = isOpenStatus && allowUserToMarkEnrollmentRequestInReview;
  const markAsUrgentEnabled = useFeatureFlag('pays-enrollment-mark-as-urgent');

  const providerId = useCurrentProviderId();
  const { data: clientRelationshipData } = useFind(
    'client_relationship',
    {
      person: enrollmentRequest.person.id,
      provider: providerId,
    },
    {
      queryConfig: {
        enabled: !!enrollmentRequest.person.id && !!providerId && pays6096UpdateEWQDesign,
        placeholderData: undefined,
      },
    },
  );

  const clientRelationships = get(clientRelationshipData, 'data.data', []);
  const showClientFacesheetLink = clientRelationships.length > 0;

  const handleClientNameClick = () => {
    if (onClientNameClick) {
      onClientNameClick(enrollmentRequest.person);
    }
  };

  const { renderClientName } = useClientNameRenderer({
    clientData: enrollmentRequest.person,
    hasAccess: showClientFacesheetLink,
    onDrawerOpen: handleClientNameClick,
    additionalProps: {
      nameFormat: 'lastFirst',
    },
  });

  usePopulate('insurance', 'insurance', enrollmentRequest, { queryConfig: { placeholderData: undefined } });
  usePopulate('person', 'person', enrollmentRequest, { queryConfig: { placeholderData: undefined } });
  usePopulate('plan', 'plan', enrollmentRequest, { queryConfig: { placeholderData: undefined } });
  usePopulate(
    'enrollment_disenroll_reason',
    'enrollment_disenroll_reason',
    enrollmentRequest,
    { queryConfig: { placeholderData: undefined } },
  );
  usePopulate(
    'enrollment_rejection_reason',
    'enrollment_rejection_reason',
    enrollmentRequest,
    { queryConfig: { placeholderData: undefined } },
  );
  usePopulate('requester', 'employee', enrollmentRequest, { queryConfig: { placeholderData: undefined } });
  usePopulate('provider', 'provider', enrollmentRequest.requester, { queryConfig: { placeholderData: undefined } });
  usePopulate(
    'enrollment_review_reason',
    'enrollment_review_reason',
    enrollmentRequest,
    { queryConfig: { placeholderData: undefined } },
  );

  const reviewEnrollmentRequest = useReviewEnrollmentRequest(false);

  const handleReviewDetailsSubmit = async (data) => reviewEnrollmentRequest(
    enrollmentRequest,
    data.review_reason,
    data.in_review_note,
    data.assignee === REQUEST_UNASSIGNED || !data.assignee ? null : data.assignee,
  );
  const assigneeOptions = useAssigneeOptions('enrollments');
  const reviewReasonOptions = useEnrollmentReviewReasonOptions(enrollmentRequest.plan.id);

  const isInsurancePopulated = !!enrollmentRequest.insurance?.state;
  const isPersonPopulated = !!enrollmentRequest.person?.last_name;
  const isPlanPopulated = !!enrollmentRequest.plan?.state;
  const isDisenrollReasonPopulated = (
    !enrollmentRequest.enrollment_disenroll_reason?.id || !!enrollmentRequest.enrollment_disenroll_reason?.code
  );
  const isRejectionReasonPopulated = (
    !enrollmentRequest.enrollment_rejection_reason?.id || !!enrollmentRequest.enrollment_rejection_reason?.code
  );

  const { isLoading: isPrimaryInsuranceLoading, primaryInsurance } = useSccRequiredInsurancePlan(
    enrollmentRequest?.insurance?.primary_health_insurance?.id,
    enrollmentRequest?.person?.id,
    enrollmentRequest?.plan?.required_health_plans,
  );
  usePopulate('plan', 'plan', primaryInsurance, {
    queryConfig: {
      enabled: !isPrimaryInsuranceLoading,
      placeholderData: undefined,
    },
  });

  const externalMemberID = primaryInsurance?.external_member_id;
  const pays4252NcTracksVerification = useFeatureFlag(
    'pays-4252-temp-display-nctracks-verification-result-on-enrollment-request',
  );
  const fetchEligibilityRecord = !!primaryInsurance?.id && pays4252NcTracksVerification;
  const eligibility = useFindEligibilityForInsurance(primaryInsurance, fetchEligibilityRecord);

  const showNcTracksVerification = !!eligibility?.id;

  useEffect(() => {
    if (
      isInsurancePopulated &&
      isPersonPopulated &&
      isPlanPopulated &&
      isDisenrollReasonPopulated &&
      isRejectionReasonPopulated &&
      !isPrimaryInsuranceLoading
    ) {
      setLoading(false);
    }
  }, [
    isInsurancePopulated,
    isPersonPopulated,
    isPlanPopulated,
    isDisenrollReasonPopulated,
    isRejectionReasonPopulated,
    isPrimaryInsuranceLoading,
  ]);

  const rejectionReason = enrollmentRequest.enrollment_rejection_reason;
  const insuranceStatus = enrollmentRequest?.insurance?.insurance_status;

  const maybeDateToString = (maybeDate) => (maybeDate ? moment(maybeDate).utc().format('MM/DD/YYYY') : '');

  return (
    <>
      {isLoading ? (<Spinner />) : (
        <div className="pt-4" data-testid="enrollment-request-drawer">
          {/* Enrollment # Row */}
          <div className="flex">
            <div className="col-sm-9">
              <div className="flex pl-4 pt-2 text-xl font-extrabold text-text-blue space-x-2 items-center">
                {isDisenrollType ? (<div>Disenrollment Request</div>) : (<div>Enrollment Request</div>)}
                <Link
                  to={`/enrollments/${enrollmentRequest.id}`}
                  className="item-center underline"
                >
                  #{enrollmentRequest.short_id}
                </Link>
              </div>
            </div>
            <div className="col-xs-3">
              <AcceptEnrollmentRequestButton
                deselectEnrollment={deselectEnrollment}
                enrollmentRequest={enrollmentRequest}
                setDrawerOpen={setDrawerOpen}
                selectedEnrollments={selectedEnrollments}
              />
            </div>
          </div>

          {/* Enrollment Navigation Row */}
          <div className="flex py-2 -ml-2">
            <div className="col-xs-1">
              <button
                disabled={getPrevRequest === undefined}
                onClick={getPrevRequest}
                type="button"
                aria-label="go to previous request"
              >
                <Icon
                  className={cx(
                    iconClass,
                    getPrevRequest ? 'hover:text-dark-grey' : 'opacity-50 cursor-not-allowed',
                  )}
                  icon="V2ChevronLeft"
                />
              </button>
            </div>
            <div className="flex col-xs-10">
              <div className="-ml-5 pr-4 text-sm">
                Last updated: {formatDate(enrollmentRequest.updated_at)}
              </div>
              <div className="pl-2">
                <EnrollmentStatusIndicator
                  status={enrollmentRequest.enrollment_status}
                />
              </div>
            </div>
            <div className="col-xs-1 ml-2">
              <button
                disabled={getNextRequest === undefined}
                onClick={getNextRequest}
                type="button"
                aria-label="go to next request"
              >
                <Icon
                  className={cx(
                    iconClass,
                    getNextRequest ? 'hover:text-dark-grey' : 'opacity-50 cursor-not-allowed',
                  )}
                  icon="V2ChevronRight"
                />
              </button>
            </div>
          </div>

          <div className="flex px-8 py-2 space-x-4">
            <RejectEnrollmentRequestButton
              deselectEnrollment={deselectEnrollment}
              enrollmentRequest={enrollmentRequest}
              setDrawerOpen={setDrawerOpen}
              selectedEnrollments={selectedEnrollments}
            />
            {
              showReviewEnrollmentRequest && (
                <ReviewEnrollmentRequest enrollmentRequest={enrollmentRequest} />
              )
            }
            {markAsUrgentEnabled &&
              [ENROLLMENT_STATUSES.open, ENROLLMENT_STATUSES.inReview]
                .includes(enrollmentRequest.enrollment_status) && (
                <MarkEnrollmentAsUrgentCheckbox
                  enrollmentId={enrollmentRequest.id}
                  isUrgent={enrollmentRequest.urgent ?? false}
                />
              )}
          </div>
          <div className="flex px-8 pt-1 pb-4">
            <AssignUserToEnrollment enrollmentRequest={enrollmentRequest} />
          </div>

          <div className="bg-gray-100 flex-1 px-3 py-3 border-t border-solid border-dark-border-blue">
            {enrollmentRequest?.enrollment_status === ENROLLMENT_STATUSES.rejected && (
              <Card borderColor="border-red" className="mb-3 px-4 py-2 bg-white">
                <CardHeader title="Rejection Details" titleIcon="Warning" iconColor="#CF2A2A" />
                <div className="py-4 space-y-2">
                  <CardDetail label="Rejection Reason">
                    {rejectionReason.display_name}
                  </CardDetail>
                  <CardDetail label="Note">
                    {enrollmentRequest.rejection_note}
                  </CardDetail>
                </div>
              </Card>
            )}
            {/* In Review Details */}
            {
              enrollmentRequest.enrollment_status === ENROLLMENT_STATUSES.inReview &&
              allowUserToMarkEnrollmentRequestInReview && (
                <>
                  <ReviewReasonDetailsCard
                    reviewReason={enrollmentRequest.enrollment_review_reason}
                    inReviewNote={enrollmentRequest.in_review_note}
                    assignee={enrollmentRequest.assignee}
                    onSubmit={handleReviewDetailsSubmit}
                    assigneeOptions={assigneeOptions}
                    reviewReasonOptions={reviewReasonOptions}
                    requiresEnrollmentDrawerClass
                  />
                  <div className="px-4 py-2 bg-white" />
                </>
              )
            }

            {/* Client Details */}
            <Card className="px-4 py-2 bg-white">
              <CardHeader title="Client Details" />
              <div className="py-4 space-y-2">
                <CardDetail label="Client Name">
                  {renderClientName()}
                </CardDetail>
                {externalMemberID && (
                  <CardDetail label="Insurance Member ID">
                    {isEmpty(externalMemberID) ? 'Insurance Member ID' : externalMemberID}
                  </CardDetail>
                )}
                {primaryInsurance && (
                  <CardDetail label="Insurance Plan">
                    {primaryInsurance.plan?.name}
                  </CardDetail>
                )}
                <CardDetail label="Date of Birth">
                  {epochToDate(get(enrollmentRequest, 'person.date_of_birth'))}
                </CardDetail>
              </div>
            </Card>

            {/* Request Details */}
            <Card className="mt-3 px-4 py-2 bg-white">
              <CardHeader title="Request Details" />
              <div className="py-4 space-y-2">
                <CardDetail label="Request Type">{startCase(enrollmentRequest.request_type)}</CardDetail>
                <CardDetail label="Date of Request">{maybeDateToString(enrollmentRequest.created_at)}</CardDetail>
                {isOpenStatus &&
                  <CardDetail label="Time in Queue">{getTimeDifference(enrollmentRequest.created_at)}</CardDetail>}
                <CardDetail label="Submitted By">
                  {enrollmentRequest.requester?.full_name}
                  <br />
                  ({enrollmentRequest.requester?.provider?.name})
                </CardDetail>
                <CardDetail label="Request ID#">{enrollmentRequest.short_id}</CardDetail>
                {isDisenrollType && (
                  <>
                    <CardDetail label="Disenrollment Reason">
                      {get(enrollmentRequest, 'enrollment_disenroll_reason.display_name')}
                    </CardDetail>
                    <CardDetail label="Note">{enrollmentRequest.disenroll_note}</CardDetail>
                  </>
                )}
              </div>
            </Card>
            {
              showNcTracksVerification && (
                <Card className="mt-3 px-4 py-2 bg-white">
                  <CardHeader title="Insurance Verification" />
                  <NcTracksVerification eligibility={eligibility} />
                </Card>
              )
            }

            {/* Social Care Coverage Details */}
            <Card className="mt-3 px-4 py-2 bg-white">
              <CardHeader title="Social Care Coverage Details" />
              <div className="py-4 space-y-2">
                <CardDetail label="Social Care Coverage Plan">{get(enrollmentRequest, 'plan.name')}</CardDetail>
                {enrollmentRequest.insurance.enrolled_at && (
                  <CardDetail label="Start Date">
                    {maybeDateToString(enrollmentRequest.insurance?.enrolled_at)}
                  </CardDetail>
                )}
                {enrollmentRequest.insurance.expired_at && (
                  <CardDetail label="End Date">
                    {maybeDateToString(enrollmentRequest.insurance?.expired_at)}
                  </CardDetail>
                )}
                <CardDetail label="Social Care Coverage Status">
                  {getClientStatusText(insuranceStatus)}
                </CardDetail>
              </div>
            </Card>
          </div>
        </div>
      )}
    </>
  );
};

EnrollmentDrawerDetails.propTypes = {
  deselectEnrollment: PropTypes.func,
  enrollmentRequest: PropTypes.shape({
    id: PropTypes.string.isRequired,
    enrollment_status: PropTypes.string.isRequired,
    urgent: PropTypes.bool,
    request_type: PropTypes.string,
    short_id: PropTypes.string,
    updated_at: PropTypes.string,
    created_at: PropTypes.string,
    rejection_note: PropTypes.string,
    disenroll_note: PropTypes.string,
    in_review_note: PropTypes.string,
    person: PropTypes.shape({
      id: PropTypes.string,
      last_name: PropTypes.string,
    }),
    plan: PropTypes.shape({
      id: PropTypes.string,
      state: PropTypes.string,
      required_health_plans: PropTypes.array,
    }),
    insurance: PropTypes.shape({
      state: PropTypes.string,
      insurance_status: PropTypes.string,
      enrolled_at: PropTypes.string,
      expired_at: PropTypes.string,
      primary_health_insurance: PropTypes.shape({
        id: PropTypes.string,
      }),
    }),
    requester: PropTypes.shape({
      full_name: PropTypes.string,
      provider: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
    enrollment_disenroll_reason: PropTypes.shape({
      id: PropTypes.string,
      code: PropTypes.string,
    }),
    enrollment_rejection_reason: PropTypes.shape({
      id: PropTypes.string,
      code: PropTypes.string,
      display_name: PropTypes.string,
    }),
    enrollment_review_reason: PropTypes.shape({}),
    assignee: PropTypes.shape({}),
  }).isRequired,
  setDrawerOpen: PropTypes.func.isRequired,
  selectedEnrollments: PropTypes.array,
  getNextRequest: PropTypes.func,
  getPrevRequest: PropTypes.func,
  onClientNameClick: PropTypes.func,
};

EnrollmentDrawerDetails.defaultProps = {
  deselectEnrollment: () => {},
  getNextRequest: undefined,
  getPrevRequest: undefined,
  selectedEnrollments: [],
  onClientNameClick: null,
};

export default EnrollmentDrawerDetails;
